import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { WidgetLocation } from '@frontend/sports/types/components/widget';
import { OnAppInit } from '@frontend/vanilla/core';
import { Observable, ReplaySubject, Subject, debounceTime, delay, filter, mergeMap, of, race, switchMap, take, tap, timer } from 'rxjs';

import { WidgetLoaderService } from '../widget/core/widget-loader.service';
import { WidgetPageService } from '../widget/core/widget-page.service';
import { MainContentLoaded } from './main-content-loaded.service';

@Injectable({ providedIn: 'root' })
export class RunAfterLcpService implements OnAppInit {
    private readonly ngZone = inject(NgZone);
    #widgetLoader = inject(WidgetLoaderService);
    #widgetPage = inject(WidgetPageService);
    #mainContentLoaded = inject(MainContentLoaded);
    #router = inject(Router);

    private readonly afterLcpCallbacks$ = new Subject<() => void>();
    private readonly lcp$$ = new ReplaySubject<void>(1);
    readonly lcp$ = this.lcp$$.asObservable();

    constructor() {
        this.afterLcpCallbacks$.pipe(mergeMap((cb) => this.lcp$.pipe(tap(() => cb())))).subscribe();
    }

    onAppInit(): void {
        this.ngZone.runOutsideAngular(() => {
            const lcp$ = new Observable<void>((observer) => {
                const performanceObserver = new PerformanceObserver((list) => {
                    // filter out splash loader
                    const otherThanSplash = list.getEntries().filter((entry) => {
                        return (
                            !(entry as any).element ||
                            !Array.from((entry as any).element.classList)
                                .join(' ')
                                .includes('splash-')
                        );
                    });
                    if (otherThanSplash.length > 0) {
                        observer.next();
                    }
                });
                performanceObserver.observe({ type: 'largest-contentful-paint', buffered: true });
                return () => {
                    performanceObserver.disconnect();
                };
            });
            // we let LCP w/o splash loader race against the first visible widget on the center column on screen
            // in case there is no widget (e.g. EDP page) we await the main content to be activated + a delay of 1 second
            // in case we are on a non-widget page (e.g. settings, menu, etc.) we just have a hardcoded timer of 2.5 seconds
            race(
                lcp$.pipe(debounceTime(1000)),
                this.#router.events.pipe(
                    filter((e) => e instanceof NavigationEnd),
                    take(1),
                    switchMap((e) => {
                        const url = this.#router.parseUrl(e.url);
                        const segment = url.root.children.primary?.segments[1]?.path;
                        if (segment === 'sports' || segment === 'esports') {
                            return this.#mainContentLoaded.loaded$.pipe(
                                // wait for widget-layout to boot
                                delay(10),
                                switchMap(() => {
                                    if (this.#widgetPage.page && this.#widgetPage.center.filter((w) => w.visible).length > 0) {
                                        return (
                                            this.#widgetLoader
                                                .widgetLoadedDetails$(WidgetLocation.Center)
                                                // if there are no widgets, we need to move on. If there are widgets, we wait for the
                                                // first one to be visible
                                                .pipe(filter((widgetData) => widgetData.filter((widget) => widget.isVisible).length > 0))
                                        );
                                    }
                                    return of(null);
                                }),
                            );
                        }
                        return timer(2500);
                    }),
                ),
            )
                .pipe(take(1))
                .subscribe(() => this.lcp$$.next());
        });
    }

    runAfterLcp(fn: () => void): void {
        this.afterLcpCallbacks$.next(fn);
    }
}
